/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect, useState, useRef} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage} from '../modules/auth'
import {App} from '../App'
import { RootState } from '../../setup/redux/RootReducer';
import { LandingPage } from '../data/LandingPage'
import axios from 'axios'
import { actionTypes } from '../modules/auth/redux/PagesRedux'
import { initialValues } from '../helpers/createNewBookingFormik'
import { TypeOptions } from '../data/TypeOptions'
import { CountryOptions, updateCountryOptions } from '../data/CountryOptions';
import { UserModel } from '../../app/modules/auth/models/UserModel'
import { defaultsBookings } from '../data/DefaultBookings'

const {PUBLIC_URL} = process.env
const DEFAULTS = '/applet/v1/defaults'
const GET_CONTACTS = '/applet/v1/database/contacts'

const AppRoutes: FC = () => {
  const isAuthorized = useSelector<any>(({auth}) => auth.accessToken, shallowEqual)
  const dispatch = useDispatch()
  const defaults = useSelector<RootState>(({pages}) => pages.defaults) as any
  const [currentLandingDefault, setCurrentLandingDefault] = useState([])
  const accessToken = useSelector<RootState>(({auth}) => auth.accessToken, shallowEqual)
  const accessTokenLS = JSON.parse(localStorage.getItem('primus-key'))?.accessToken  
  const user: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const initializedRef = useRef(false)

  const getCountryOptionsAllowed = () => {
    let defaultCountries = user?.defaultCountries
    let countriesArrayLocal = []
    defaultCountries?.forEach(country => {
        let allowedCountry = CountryOptions?.find(c => c.CodePrimus === country)
        if (allowedCountry) {
          countriesArrayLocal?.push(allowedCountry)
        }
    })
    return countriesArrayLocal.sort((a, b) => a.Name.localeCompare(b.Name))
  }  

  useEffect(() => {

    if( window?.location?.href.match(/ecu/gi) ) {
      document.title = 'Customer Portal';
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) 
  
  useEffect(() => {
    if(accessToken && user && !initializedRef.current) {
          initializedRef.current = true;
          axios.get('/api/v1/tools/countries').then(result => {
          const apiCountries = result.data.data.results?.map((c: any) => ({
            Name: c.name,
            Code: c.ISO2Code,
            CodePrimus: c.ISO3Code,
            hasStates: c.hasStates,
            hasZipcodes: c.hasZipcodes
          }))

          if (apiCountries?.length) {
            updateCountryOptions(apiCountries)
          }
        }).catch(() => {
        })

        let otherCountry = getCountryOptionsAllowed().find(c => c?.CodePrimus === 'USA') ? 'USA' : getCountryOptionsAllowed()[0]?.CodePrimus || 'USA'
        
        if(defaults.length === 0 && (accessTokenLS ? JSON.parse(accessTokenLS) : accessToken)){
          // let hasBookingChanged = false
          axios.get(DEFAULTS).then((results) => {
            let defaults = results.data.data.results
            // defaultsBookings.map(function(item) {
            //   const filterExist = defaults.bookings?.find((element) => element.name == item.name)
            //   if(!filterExist) {
            //     hasBookingChanged = true
            //     defaults.bookings?.push(item)
            //   }
            // })
          
          // if(hasBookingChanged) {
          //   axios.post(DEFAULTS, {
          //     ...defaults
          //   }).then(() => {
          //     dispatch({type: actionTypes.SetDefaults, payload: {...defaults }})
          //   }).catch(error => {
              
          //   })
          // }
            
            if(!user?.allowTrack && defaults.landingPage == 'TRACKING') {
              defaults.landingPage = 'Dashboard'
              axios.post(DEFAULTS, {
                ...defaults
              })
              dispatch({type: actionTypes.SetDefaults, payload: {...defaults, landingPage: 'Dashboard'}})
            }

            const hasDrayageVendors = user?.enabledVendors?.some((vendor: any) =>
              vendor.type === 'DRAY' || vendor?.subVendors?.some((subVendor: any) => subVendor?.carrierType === 'DRAY')
            )
            if(!hasDrayageVendors && defaults.landingPage == 'DRAY') {
              defaults.landingPage = 'Dashboard'
              axios.post(DEFAULTS, {
                ...defaults
              })
              dispatch({type: actionTypes.SetDefaults, payload: {...defaults, landingPage: 'Dashboard'}})
            }

            // console.log(LandingPage.find(page => page.value === defaults.landingPage)?.path || 'Dashboard')

            dispatch({type: actionTypes.SetDefaults, payload: {...defaults,  
              landingPage: defaults.landingPage || 'Dashboard',
              landing: LandingPage.find(page => page.value === defaults.landingPage || 'Dashboard')?.path || '/home' }})

            let payload = {
              ...initialValues,
              freightInfo: [{
                  ...initialValues.freightInfo[0],
                  dimType: defaults?.packageType ? TypeOptions.find(type => type.value === defaults?.packageType) : {
                    name: "PALLET",
                    value: "PLT"
                  },
                  L: defaults?.length,
                  W: defaults?.width,
                  H: defaults?.height,
                  Lus: defaults?.length,
                  Wus: defaults?.width,
                  Hus: defaults?.height,
                  commodity: defaults?.commodity
              }],
              accessorials: defaults.accessorials,
              shipper: {
                ...defaults.defaultBookingShipperLocation,
                country: defaults.defaultBookingShipperLocation?.country || otherCountry
              },
              consignee: {
                ...defaults.defaultBookingConsigneeLocation,
                country: defaults.defaultBookingConsigneeLocation?.country || otherCountry
              },
              pickupDate: {
                type: 'PickupOn',
                date: defaults.pickupDateToday ? new Date() : null,
                from: defaults?.defaultBookingShipperLocation?.opentime || defaults.pickupTimeFrom || '',
                to: defaults?.defaultBookingShipperLocation?.closetime || defaults.pickupTimeTo || ''
              },
              deliveryDate: {
                type: 'DeliveryBy',
                date: null,
                from: defaults.defaultBookingConsigneeLocation?.opentime || '',
                to: defaults.defaultBookingConsigneeLocation?.closetime || ''
              },
              pickupDateToday: defaults?.pickupDateToday,
              pickupTimeFrom: defaults.pickupTimeFrom,
              pickupTimeTo: defaults.pickupTimeTo
            }

            setCurrentLandingDefault(defaults)

            // Fetch HAZ contact for default shipper if exists
            if(defaults.defaultBookingShipperLocation?.id){
              axios.get(GET_CONTACTS, {
                params: { shippingLocationId: defaults.defaultBookingShipperLocation.id }
              }).then(results => {
                const contacts = results.data.data.results
                const hazContact = contacts?.find(contact => contact.type === "Emergency/Haz")
                if(hazContact){
                  payload.emergencyContact = `${hazContact.firstName} ${hazContact.lastName}`
                  payload.emergencyPhone = hazContact.phone
                }
                dispatch({type: actionTypes.SetNewBookingValues, payload: payload})
                dispatch({type: actionTypes.SetInitialNewBooking, payload: payload})
              }).catch(() => {
                dispatch({type: actionTypes.SetNewBookingValues, payload: payload})
                dispatch({type: actionTypes.SetInitialNewBooking, payload: payload})
              })
            } else {
              dispatch({type: actionTypes.SetNewBookingValues, payload: payload})
              dispatch({type: actionTypes.SetInitialNewBooking, payload: payload})
            }
          }).catch(() => {
            dispatch({type: actionTypes.SetDefaults, payload: { landingPage: 'Dashboard', landing: '/home' }})
          })
        }
    }
  }, [accessToken, user])
  
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {user && isAuthorized ? (
            <>
              <Route path='/*' element={<PrivateRoutes user={user} />} />
              <Route index element={<Navigate
                  to={LandingPage["a"] ? LandingPage["a"].find(page => page.value === defaults.landingPage)?.path || '/home'
                  :
                  LandingPage.find(page => page.value === defaults.landingPage)?.path || '/home' }
                />}
              />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
